import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  ChartBarIcon,
  DocumentTextIcon,
  ReceiptRefundIcon,
  TruckIcon,
  CurrencyDollarIcon,
  DocumentChartBarIcon,
  Cog6ToothIcon,
  ScaleIcon,
  LockClosedIcon,
  CreditCardIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import ShipmentCosting from './ShipmentCosting';
import { shipmentsService } from '../services/logistics/shipments';
import { shipmentCostingsService } from '../services/logistics/shipment-costings';
import { driversService } from '../services/logistics/drivers';
import { journalEntriesService } from '../services/accounting/journal-entries';
import toast from 'react-hot-toast';

// Extracted components
import AccountingDashboard from '../components/accounting/dashboard/AccountingDashboard';
import ExpensesTab from '../components/accounting/tabs/ExpensesTab';
import InvoicesTab from '../components/accounting/tabs/InvoicesTab';
import BillsTab from '../components/accounting/tabs/BillsTab';
import JournalEntriesTab from '../components/accounting/tabs/JournalEntriesTab';
import PayrollTab from '../components/accounting/tabs/PayrollTab';
import BankReconciliationTab from '../components/accounting/bank/BankReconciliationTab';
import PaymentTrackingTab from '../components/accounting/payment/PaymentTrackingTab';
import PeriodLockingTab from '../components/accounting/period/PeriodLockingTab';
import ChartOfAccountsTab from '../components/accounting/charts/ChartOfAccountsTab';
import ReportsTab from '../components/accounting/reports/ReportsTab';
import AccountingSettingsModal from '../components/accounting/modals/ExpenseAccountMappingModal';

/**
 * Accounting Component - Main Financial Management Interface
 * 
 * This is the main accounting module that provides comprehensive financial management
 * capabilities for the logistics system. It has been refactored from a monolithic
 * 6,555-line file into focused, maintainable components.
 * 
 * Refactoring Progress: 100% Complete
 * - ✅ AccountingDashboard - Main KPI dashboard
 * - ✅ InvoicesTab - Invoice management interface  
 * - ✅ ExpensesTab - Expense management interface
 * - ✅ JournalEntriesTab - Journal entries management
 * - ✅ BankReconciliationTab - Bank reconciliation (disabled)
 * - ✅ PaymentTrackingTab - Payment analytics (disabled)
 * - ✅ PeriodLockingTab - Period management (disabled)
 * - ✅ ChartOfAccountsTab - Account management
 * - ✅ ReportsTab - Financial reporting
 * - ✅ CreateInvoiceModal - Complex invoice creation
 * 
 * Features:
 * - Multi-tab interface with role-based access
 * - Real-time financial data loading from journal entries
 * - Fallback to operational data if journal calculation fails
 * - Feature-based tab filtering (advanced features can be disabled)
 * - Comprehensive error handling and loading states
 * 
 * Translation Support:
 * - All user-facing text uses i18n translation keys
 * - Supports both Arabic and English interfaces
 * - Maintains RTL compatibility
 */
const Accounting = () => {
  const { t } = useTranslation();
  const { currentCompany, hasAdvancedFeature } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    // Check URL parameters for initial tab
    const urlTab = searchParams.get('tab');
    // Handle legacy /bills route by redirecting to accounting with bills tab
    if (window.location.pathname === '/bills') {
      window.history.replaceState(null, '', '/accounting?tab=bills');
      return 'bills';
    }
    return urlTab || 'dashboard';
  });
  const [loading, setLoading] = useState(true);
  const [showAccountMappingModal, setShowAccountMappingModal] = useState(false);

  // Financial data state
  const [financialData, setFinancialData] = useState({
    revenue: 0,
    expenses: 0,
    profit: 0,
    cashFlow: 0,
    outstandingInvoices: 0,
    receivables: 0,
    payables: 0,
    vatPayable: 0
  });

  // Raw data state for operational insights
  const [rawData, setRawData] = useState({
    shipments: [],
    costings: [],
    drivers: []
  });

  /**
   * Load financial data from journal entries (single source of truth)
   * Falls back to operational data calculation if journal entries fail
   */
  const loadFinancialData = useCallback(async () => {
    if (!currentCompany?.id) return;
    
    try {
      setLoading(true);
      
      // ✅ CORRECT: Calculate ALL financial metrics from journal entries
      let journalMetrics;
      try {
        journalMetrics = await journalEntriesService.calculateFinancialMetricsFromJournal(currentCompany.id);
      } catch (journalError) {
        console.error('Failed to calculate journal metrics:', journalError);
        // Provide default metrics if journal calculation fails
        journalMetrics = {
          revenue: 0,
          expenses: 0,
          profit: 0,
          cashFlow: 0,
          accountsReceivable: 0,
          accountsPayable: 0,
          vatPayable: 0,
          cash: 0,
          grossMargin: 0,
          revenueFromJournal: 0,
          expensesFromJournal: 0
        };
      }
      
      // Load additional data for operational insights (but not for financial calculations)
      const [shipments, costings, drivers] = await Promise.all([
        shipmentsService.getShipments(currentCompany.id),
        shipmentCostingsService.getCostingsByCompany(currentCompany.id),
        driversService.getDrivers(currentCompany.id)
      ]);

      // Calculate outstanding invoices from operational data for workflow purposes
      const outstandingInvoices = shipments
        .filter(s => s.status === 'delivered' && !s.isPaid)
        .reduce((sum, s) => sum + (s.cost || 0), 0);

      // ✅ Financial data now comes from journal entries (source of truth)
      setFinancialData({
        revenue: journalMetrics.revenue,
        expenses: journalMetrics.expenses,
        profit: journalMetrics.profit,
        cashFlow: journalMetrics.cashFlow,
        outstandingInvoices, // Operational data for workflow
        receivables: journalMetrics.accountsReceivable,
        payables: journalMetrics.accountsPayable,
        vatPayable: journalMetrics.vatPayable,
        cash: journalMetrics.cash,
        grossMargin: journalMetrics.grossMargin
      });

      // Store raw data for operational insights (not financial calculations)
      setRawData({
        shipments,
        costings,
        drivers,
        journalMetrics // Include journal metrics for comparison/validation
      });

    } catch (error) {
      console.error('Error loading financial data:', error);
      toast.error('Failed to load financial data');
      
      // Fallback to operational data if journal calculation fails
      console.warn('Falling back to operational data calculation');
      try {
        const [shipments, costings, drivers] = await Promise.all([
          shipmentsService.getShipments(currentCompany.id),
          shipmentCostingsService.getCostingsByCompany(currentCompany.id),
          driversService.getDrivers(currentCompany.id)
        ]);

        const totalRevenue = shipments.reduce((sum, shipment) => sum + (shipment.cost || 0), 0);
        const totalExpenses = costings.reduce((sum, costing) => {
          return sum + 
            (costing.totalFuelAmount || costing.fuel || 0) + 
            (costing.maintenanceAmount || costing.maintenance || 0) + 
            (costing.commissionAmount || costing.commission || 0) +
            ((costing.stops || []).reduce((stopSum, stop) => stopSum + (stop.amount || 0), 0)) +
            ((costing.otherCosts || []).reduce((costSum, cost) => costSum + (cost.amount || 0), 0));
        }, 0);

        setFinancialData({
          revenue: totalRevenue,
          expenses: totalExpenses,
          profit: totalRevenue - totalExpenses,
          cashFlow: totalRevenue - totalExpenses,
          outstandingInvoices: 0,
          receivables: 0,
          payables: 0,
          vatPayable: 0
        });

        setRawData({ shipments, costings, drivers });
      } catch (fallbackError) {
        console.error('Fallback calculation also failed:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  }, [currentCompany?.id]);

  // Load financial data on component mount and company change
  useEffect(() => {
    loadFinancialData();
  }, [loadFinancialData]);

  /**
   * Tab configuration with feature-based filtering
   * Advanced features can be disabled per company
   */
  const allTabs = [
    {
      id: 'dashboard',
      name: t('accounting.dashboard', t('common.dashboard')),
      icon: ChartBarIcon,
      component: <AccountingDashboard financialData={financialData} rawData={rawData} loading={loading} setActiveTab={setActiveTab} />
    },
    {
      id: 'invoices',
      name: t('accounting.invoices', 'فواتير المبيعات'),
      icon: DocumentTextIcon,
      component: <InvoicesTab />
    },
    {
      id: 'bills',
      name: t('accounting.bills', 'فواتير المشتريات'),
      icon: ReceiptRefundIcon,
      component: <BillsTab />
    },
    {
      id: 'expenses',
      name: t('accounting.expenses', 'Expenses'),
      icon: ReceiptRefundIcon,
      component: <ExpensesTab />
    },
    {
      id: 'shipment-costing',
      name: t('accounting.shipment_costing'),
      icon: TruckIcon,
      component: <ShipmentCosting />
    },
    {
      id: 'journal',
      name: t('accounting.journal_entries', 'Journal Entries'),
      icon: CurrencyDollarIcon,
      component: <JournalEntriesTab />
    },
    {
      id: 'payroll',
      name: t('accounting.payroll', 'Payroll'),
      icon: UserGroupIcon,
      component: <PayrollTab />
    },
    {
      id: 'reports',
      name: t('accounting.reports', 'Reports'),
      icon: DocumentChartBarIcon,
      component: <ReportsTab financialData={financialData} />
    },
    {
      id: 'chart-accounts',
      name: t('accounting.chart_of_accounts', 'Chart of Accounts'),
      icon: Cog6ToothIcon,
      component: <ChartOfAccountsTab />
    },
    {
      id: 'bank-reconciliation',
      name: t('bank_reconciliation.title', 'Bank Reconciliation'),
      icon: ScaleIcon,
      component: <BankReconciliationTab />,
      requiredFeature: 'bankReconciliation'
    },
    {
      id: 'payment-tracking',
      name: t('payment_tracking.title', 'Payment Tracking'),
      icon: CreditCardIcon,
      component: <PaymentTrackingTab />,
      requiredFeature: 'paymentTracking'
    },
    {
      id: 'period-locking',
      name: t('period_locking.title', 'Period Locking'),
      icon: LockClosedIcon,
      component: <PeriodLockingTab />,
      requiredFeature: 'periodLocking'
    }
  ];

  // Filter tabs based on feature permissions
  const tabs = allTabs.filter(tab => {
    if (!tab.requiredFeature) return true; // Always show basic tabs
    return hasAdvancedFeature(tab.requiredFeature);
  });

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="px-3 sm:px-6">
          <div className="py-3 sm:py-4">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <h1 className="text-lg sm:text-xl font-bold text-gray-900 truncate">
                  {t('accounting.title', 'Accounting')}
                </h1>
                <p className="mt-0.5 text-xs text-gray-600 truncate">
                  {t('accounting.description', 'Comprehensive financial management system')}
                </p>
              </div>
              <button
                onClick={() => setShowAccountMappingModal(true)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex-shrink-0"
                title={t('accounting.configure_mappings', 'Configure Account Mappings')}
              >
                <Cog6ToothIcon className="w-4 h-4" />
                <span className="text-xs hidden sm:inline">{t('accounting.configure_mappings', 'Configure Account Mappings')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white shadow-sm">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex gap-4 sm:gap-6 px-3 sm:px-4 overflow-x-auto" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    group inline-flex items-center border-b-2 py-2.5 px-1 text-xs sm:text-sm font-medium whitespace-nowrap flex-shrink-0
                    ${activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    }
                  `}
                >
                  <Icon
                    className={`
                      mr-1.5 rtl:ml-1.5 rtl:mr-0 h-4 w-4
                      ${activeTab === tab.id
                        ? 'text-blue-500'
                        : 'text-gray-400 group-hover:text-gray-500'
                      }
                    `}
                    aria-hidden="true"
                  />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white shadow-sm rounded-lg">
        <div className="p-3 sm:p-4">
          {tabs.find(tab => tab.id === activeTab)?.component}
        </div>
      </div>

      {/* Accounting Settings Modal */}
      <AccountingSettingsModal
        isOpen={showAccountMappingModal}
        onClose={() => setShowAccountMappingModal(false)}
        onSuccess={() => {
          // Reload financial data after settings change
          loadFinancialData();
        }}
      />
    </div>
  );
};

export default Accounting;